import Processo, { ProcessoUtils, StatusProcesso } from '../model/processo/Processo';
import api from '../api';

export interface ProcessoCriacaoData {
    nome: string;
    unidade: string;
    responsavel: string;
    identificador: string;
    editais: { nome: string; categoria: string; hasChamadas: boolean }[];
    contasServicoIds?: string[];
    configuracaoAcessoCandidato?: {
        modo?: 'GLOBAL' | 'PROCESSO';
        janelaLiberacaoHoras?: number;
        envioFormularioRecursoPorEmail?: boolean;
        prazoInterposicaoRecursoValor?: number;
        prazoInterposicaoRecursoUnidade?: 'HORAS' | 'DIAS';
        envioAutomaticoResultadoPorEtapaHabilitado?: boolean;
    };
}

interface ProcessoPatch {
    nome: string;
    unidade: string;
    responsavel: string;
    status: string
    identificador: string;
    configuracaoAcessoCandidato?: {
        modo?: 'GLOBAL' | 'PROCESSO';
        janelaLiberacaoHoras?: number;
        envioFormularioRecursoPorEmail?: boolean;
        prazoInterposicaoRecursoValor?: number;
        prazoInterposicaoRecursoUnidade?: 'HORAS' | 'DIAS';
        envioAutomaticoResultadoPorEtapaHabilitado?: boolean;
    };
}

export class ProcessoViewModel {
    static async criarProcesso(dados: ProcessoCriacaoData): Promise<Processo> {
        try {
            const response = await api.post("/processos", dados);
    
            if (response.status < 200 || response.status >= 300) {
                throw new Error(`Erro ao criar processo: ${response.statusText}`);
            }
    
            const processo = response.data;    
            processo.status = this.formatarStatusR(processo.status);
    
            return processo;
        } catch (error) {
            console.error('Erro ao criar processo:', error);
            throw error;
        }
    }

    private static getEnumValue(enumObj: any, value: any): string {
        return enumObj[value] || value;
    }

    static async atualizarProcesso(id: string, dados: Processo) {
        try {
            const dadosParaAtualizacao: ProcessoPatch = {
                nome: dados.nome,
                unidade: dados.unidade.id!,
                responsavel: dados.responsavel,
                status: ProcessoUtils.getStatusKeyByValue(dados.status)!,
                identificador: dados.identificador,
                configuracaoAcessoCandidato: dados.configuracaoAcessoCandidato
            };    
            const response = await api.patch(`/processos/${id}`, dadosParaAtualizacao);
            return this.formatarProcesso(response.data);
        } catch (error) {
            throw error;
        }
    }


    

    static async obterProcessoPorId(id: string) {
        try {
            const response = await api.get(`/processos/${id}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    static async obterContasServicoProcesso(id: string) {
        try {
            const response = await api.get(`/processos/${id}/contas-servico`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    static async finalizarProcesso(processoId: string): Promise<Processo> {
        try {
            const response = await api.patch(`/processos/${processoId}/finalizacao`, {
                // Caso precise passar informações adicionais
            });
    
            // Formata o status retornado pelo backend
            const processoFinalizado = response.data;
            processoFinalizado.status = this.formatarStatus(processoFinalizado.status);
    
            return processoFinalizado; // Retorna o processo atualizado já com o status formatado
        } catch (error) {
            throw error;
        }
    }

    static async cancelarProcesso(processoId: string): Promise<Processo> {
        try {
            const response = await api.patch(`/processos/${processoId}/cancelamento`);
    
            // Formata o status retornado pelo backend
            const processoCancelado = response.data;
            processoCancelado.status = this.formatarStatus(processoCancelado.status);
    
            return processoCancelado;
        } catch (error) {
            throw error;
        }
    }
    

    static async obterListaDeProcessos(
    pagination: boolean, 
    status?: StatusProcesso,
    page: number = 0,
    size: number = 10,
    nome?: string
) {
    try {
        // Construir a URL com parâmetros de paginação e filtros
        let url = `/processos?pagination=${pagination}&page=${page}&size=${size}`;
        
        if (status) {
            url += `&status=${status.toUpperCase()}`;
        }
        
        if (nome) {
            url += `&nome=${encodeURIComponent(nome)}`;
        }
        
        const response = await api.get(url);
        
        // Extrair os dados paginados da resposta
        const pageData = {
            content: response.data._embedded?.processoResponseList?.map((processo: any) => ({
                id: processo.id,
                nome: processo.nome,
                unidade: processo.unidade,
                identificador: processo.identificador,
                responsavel: processo.responsavel,
                dataCriacao: new Date(processo.dataCriacao),
                status: this.formatarStatus(processo.status),
                cronograma: processo.cronograma?.atividades || [],
                salaEspera: {
                    id: processo.salaEspera?.id,
                    salaUrl: processo.salaEspera?.link || null,
                },
                numeroCandidatos: processo.numeroCandidatos,
                    finalizavel: processo.finalizavel,
                    convocacoesEnviadas: processo.convocacoesEnviadas,
                    hasIntegracaoSiga: processo.hasIntegracaoSiga || false,
                    integracaoCandidatosAberta: processo.integracaoCandidatosAberta || false,
                    configuracaoAcessoCandidato: processo.configuracaoAcessoCandidato
                })) || [],
            pageMetadata: {
                totalElements: response.data.page?.totalElements || 0,
                totalPages: response.data.page?.totalPages || 0,
                size: response.data.page?.size || size,
                number: response.data.page?.number || page
            }
        };
        
        return pageData;
    } catch (error) {
        throw error;
    }
}

    static async buscarProcessos(filtros: {
        editalNome?: string;
        identificador?: string;
        responsavel?: string;
        unidadeId?: string;
        status?: StatusProcesso;
    }, page: number = 0, size: number = 10) {
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                size: size.toString(),
                sort: 'dataCriacao,desc'
            });

            if (filtros.editalNome) params.append('editalNome', filtros.editalNome);
            if (filtros.identificador) params.append('identificador', filtros.identificador);
            if (filtros.responsavel) params.append('responsavel', filtros.responsavel);
            if (filtros.unidadeId) params.append('unidadeId', filtros.unidadeId);
            if (filtros.status) params.append('status', filtros.status.toUpperCase());

            const response = await api.get(`/processos/filter?${params}`);
            
            return {
                content: response.data._embedded?.processoResponseList?.map((processo: any) => ({
                    id: processo.id,
                    nome: processo.nome,
                    unidade: processo.unidade,
                    identificador: processo.identificador,
                    responsavel: processo.responsavel,
                    dataCriacao: new Date(processo.dataCriacao),
                    status: this.formatarStatus(processo.status),
                    cronograma: processo.cronograma?.atividades || [],
                    salaEspera: {
                        id: processo.salaEspera?.id,
                        salaUrl: processo.salaEspera?.link || null,
                    },
                    numeroCandidatos: processo.numeroCandidatos,
                    finalizavel: processo.finalizavel,
                    convocacoesEnviadas: processo.convocacoesEnviadas,
                    nomeEditavel: processo.nomeEditavel,
                    cronogramaDefinido: processo.cronogramaDefinido,
                    bancasTerminadas: processo.bancasTerminadas,
                    editais: processo.editais || [],
                    contasServico: processo.contasServico || [],
                    hasIntegracaoSiga: processo.hasIntegracaoSiga || false,
                    integracaoCandidatosAberta: processo.integracaoCandidatosAberta || false,
                    configuracaoAcessoCandidato: processo.configuracaoAcessoCandidato
                })) || [],
                pageMetadata: {
                    totalElements: response.data.page?.totalElements || 0,
                    totalPages: response.data.page?.totalPages || 0,
                    size: response.data.page?.size || size,
                    number: response.data.page?.number || page
                }
            };
        } catch (error) {
            throw error;
        }
    }
    

    private static formatarStatus(status: string): string {
        const statusMap: { [key: string]: string } = {
            "CRIADO": StatusProcesso.CRIADO.toString(),
            "PLANEJADO": StatusProcesso.PLANEJADO.toString(),
            "DEFINIDO": StatusProcesso.DEFINIDO.toString(),
            "FACIAL": StatusProcesso.FACIAL.toString(),
            "VIRTUAL": StatusProcesso.VIRTUAL.toString(),
            "RECURSAL": StatusProcesso.RECURSAL.toString(),
            "FINALIZADO": StatusProcesso.FINALIZADO.toString(),
            "CANCELADO": StatusProcesso.CANCELADO.toString()
        };
        return statusMap[status] || status;
    }

    private static formatarStatusR(status: string): StatusProcesso {
        const statusMap: { [key: string]: StatusProcesso } = {
            "CRIADO": StatusProcesso.CRIADO,
            "PLANEJADO": StatusProcesso.PLANEJADO,
            "DEFINIDO": StatusProcesso.DEFINIDO,
            "FACIAL": StatusProcesso.FACIAL,
            "VIRTUAL": StatusProcesso.VIRTUAL,
            "RECURSAL": StatusProcesso.RECURSAL,
            "FINALIZADO": StatusProcesso.FINALIZADO,
            "CANCELADO": StatusProcesso.CANCELADO
        };
        return statusMap[status] || StatusProcesso.CRIADO;
    }

    private static formatarStatusParaBackend(status: string): string {
        return status.toUpperCase();
    }

    static formatarProcesso(processo: any): Processo {
        return {
            id: processo.id,
            nome: processo.nome,
            unidade: processo.unidade,
            responsavel: processo.responsavel,
            identificador: processo.identificador,
            dataCriacao: processo.dataCriacao,
            status: this.formatarStatusR(processo.status), 
            cronograma: processo.cronograma,
            salaEspera: processo.salaEspera,
            candidatos: [],
            hasIntegracaoSiga: processo.hasIntegracaoSiga || false,
            integracaoCandidatosAberta: processo.integracaoCandidatosAberta || false,
            configuracaoAcessoCandidato: processo.configuracaoAcessoCandidato
        };
    }

    static async associarContaServico(processoId: string, contaServicoId: string): Promise<void> {
        try {
            await api.patch(`/processos/${processoId}/conta-servico`, {
                contaServicoId
            });
        } catch (error) {
            throw error;
        }
    }

    static async atualizarContasServico(processoId: string, contasCandidatos: string[], contasFotos: string[]): Promise<void> {
        try {
            await api.patch(`/processos/${processoId}/contas-servico`, {
                contasIntegracaoCandidatos: contasCandidatos,
                contasIntegracaoFotos: contasFotos
            });
        } catch (error) {
            throw error;
        }
    }

    static async consultarIntegracaoSiga(processoId: string): Promise<boolean> {
        try {
            const response = await api.get(`/processos/${processoId}/integracao-siga`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    static async alterarIntegracaoSiga(processoId: string, hasIntegracaoSiga: boolean): Promise<void> {
        try {
            await api.patch(`/processos/${processoId}/integracao-siga`, {
                hasIntegracaoSiga
            });
        } catch (error) {
            throw error;
        }
    }

    static async consultarIntegracaoCandidatos(processoId: string): Promise<boolean> {
        try {
            const response = await api.get(`/processos/${processoId}/integracao-candidatos`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    static async abrirIntegracaoCandidatos(processoId: string): Promise<void> {
        try {
            await api.patch(`/processos/${processoId}/abrir-integracao-candidatos`);
        } catch (error) {
            throw error;
        }
    }

    static async finalizarIntegracaoCandidatos(processoId: string): Promise<void> {
        try {
            await api.patch(`/processos/${processoId}/finalizar-integracao-candidatos`);
        } catch (error) {
            throw error;
        }
    }

}
